import React, { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { usePageVisibility } from "../../hooks/usePageVisibility";
import { UserChatMessage } from "../../components/UserChatMessage";
import { Answer } from "../../components/Answer";
import { AnswerStatus } from "../../components/AnswerStatus";
import { StreamingAnswer } from "../../components/StreamingAnswer";

import { ClearChatButton } from "../../components/ClearChatButton";
import { SystemPrompt } from "../../features/SystemPrompt";
import { useSystemPromptContext } from "../../features/SystemPrompt/context/SystemPromptContext";
import { ConversationExporter } from "../../features/ConversationExporter";
import { AthenaSearchButton } from "../../products/athena/AthenaSearchButton";
import { AthenaChatButton } from "../../products/athena/AthenaChatButton";
import { QuestionInput } from "../../components/QuestionInput";
import ServiceNSWLogo from "../../assets/serviceNSWLogo";
import {
  ChatAppResponse,
  feedbackApi,
  ConversationEntry,
  ConversationEntryData,
  isFeedbackBtnType,
  streamChat,
  type StreamState,
  type ResponseMessage
} from "../../api";
import { ExampleList } from "../../components/Example";
import { Feedback } from "../../components/Feedback";
import styles from "./Chat.module.css";
import session from "../../session";
import { RAGAgent } from "../../api/types/models";

const ChatHome: React.FC<{ onQuestion: (question: string) => void }> = ({ onQuestion }) => (
  <div className={styles.chatEmptyState}>
    <ServiceNSWLogo w={"177"} h={"61"} />
    <h3>How can I help you today?</h3>
    <ExampleList onExampleClicked={onQuestion} />
  </div>
);

const Chat: React.FC = () => {
  const { savedPrompt } = useSystemPromptContext();
  const [conversation, setConversation] = useState<ConversationEntry[]>([]);
  const [generating, setGenerating] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [inside, setInside] = useState<boolean>(false);
  const [isChatHome, setChatHome] = useState<boolean>(false);
  const [hasAnswer, setHasAnswer] = useState<boolean>(false);
  const [inputFocusToggle, setInputFocusToggle] = useState<boolean>(false);
  const [streamState, setStreamState] = useState<StreamState | null>(null);

  const [, setPendingQuestions] = useState<string[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasCompletedRef = useRef<boolean>(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const updateConversationState = useCallback((conversation: ConversationEntry[] | ((prev: ConversationEntry[]) => ConversationEntry[])) => {
    if (typeof conversation === "function") {
      setConversation(prev => {
        const updated = conversation(prev);
        session.saveConversationEntries(updated);
        return updated;
      });
    } else {
      session.saveConversationEntries(conversation);
      setConversation(conversation);
    }
  }, []);

  useEffect(() => {
    setChatHome(!generating && conversation.length === 0);
    isChatHome ? setInside(false) : {};
    setHasAnswer(!!conversation[conversation.length - 1]?.[1]);
    setInputFocusToggle(prev => !prev);
    scrollToBottom();
  }, [generating, conversation, isChatHome, scrollToBottom]);

  useEffect(() => {
    const loadConversationHistory = async () => {
      const conversationHistory = session.getConversationEntries();
      if (conversationHistory) {
        setConversation(conversationHistory);
      }
    };

    loadConversationHistory();
  }, []);

  const clearChat = useCallback(() => {
    session.clear();
    session.start();
    setInside(false);
    setConversation([]);
  }, []);

  const onInputQuestion = async (q: string) => {
    // Queue if already generating
    if (generating) {
      setPendingQuestions(prev => [...prev, q]);
      return;
    }

    setStreamState(null);
    setErrorMsg(null);
    setGenerating(true);
    hasCompletedRef.current = false;
    scrollToBottom();

    // Build conversation history from prior turns for the backend
    const history: ResponseMessage[] = conversation.flatMap((entry): ResponseMessage[] => {
      const msgs: ResponseMessage[] = [{ role: "user", content: entry[0] }];
      const assistantContent = entry[1]?.choices[0]?.message?.content;
      if (assistantContent) {
        msgs.push({ role: "assistant", content: assistantContent });
      }
      return msgs;
    });

    try {
      await streamChat(
        q,
        (state: StreamState) => {
          flushSync(() => {
            setStreamState(state);
          });

          if (state.error) {
            setErrorMsg(state.error.message);
          }
          if (state.isComplete && !hasCompletedRef.current) {
            hasCompletedRef.current = true;
            const streamResponse: ChatAppResponse = {
              choices: [
                {
                  id: state.metadata?.id ?? "",
                  index: 0,
                  message: { content: state.content, role: "assistant" },
                  session_state: null,
                  sessionId: session.getId() ?? "",
                  feedback: ""
                }
              ],
              selected_agent: (state.metadata?.selectedAgent as RAGAgent) ?? RAGAgent.Legislation,
              recommendor_response: state.metadata?.recommendorResponse ?? "",
              fallback_agent: state.metadata?.fallbackAgent,
              is_system_fallback: state.metadata?.isSystemFallback?.toString()
            };
            const entryData: ConversationEntryData = {
              citations: state.citations,
              otherLinks: state.otherLinks,
              followupQuestions: state.followupQuestions
            };
            updateConversationState((prev: ConversationEntry[]) => [...prev, [q, streamResponse, entryData]]);
          }
        },
        history,
        savedPrompt ?? undefined
      );
    } catch (e) {
      console.error(e);
      setErrorMsg("Something went wrong. Please try again later.");
    } finally {
      setGenerating(false);

      // Process next queued question using functional state update
      setPendingQuestions(prev => {
        const [nextQuestion, ...rest] = prev;
        if (nextQuestion !== undefined) {
          setTimeout(() => onInputQuestion(nextQuestion), 0);
        }
        return rest;
      });
    }
  };

  const onCitationClicked = useCallback(
    async (citationPath: string, _?: string) => {
      if (generating) return;

      const openInNewWindow = (url: string) => window.open(url, "_blank", "noopener,noreferrer");

      openInNewWindow(citationPath);
    },
    [generating]
  );

  const saveChatFeedback = useCallback(
    (botResponseId: string) => async (feedback: string) => {
      if (generating) {
        return new Promise<void>(() => {});
      }

      const feedbackData = isFeedbackBtnType(feedback) ? { type: feedback } : { reason: feedback };

      return feedbackApi({ id: botResponseId, feedback: feedbackData }).then(() => {
        session.updateChatFeedback(botResponseId, feedbackData);
      });
    },
    [generating]
  );

  const isComplete = useCallback((d: ChatAppResponse) => !!d?.choices[0]?.id, []);

  const handleMouseEnter = useCallback(() => setInside(true), []);
  const handleMouseLeave = useCallback(() => setInside(false), []);

  const renderedAnswers = conversation.map((answer, index) => {
    return (
      <div className={styles.chatMessageWrapper} key={index}>
        <UserChatMessage message={answer[0]} />
        <div className={styles.chatMessageGpt}>
          {answer[1] && (
            <Answer
              content={answer[1].choices[0].message.content}
              isStreaming={false}
              agentStyle={(answer[1].selected_agent as RAGAgent) === RAGAgent.Legislation ? "legislation" : undefined}
              feedbackSlot={
                isComplete(answer[1]) ? (
                  <Feedback
                    getFeedbackData={() => session.getChatFeedbackData(answer[1]?.choices[0]?.id ?? "")}
                    save={saveChatFeedback(answer[1].choices[0].id)}
                  />
                ) : undefined
              }
              onCitationClicked={onCitationClicked}
              citations={(answer[2] as ConversationEntryData | undefined)?.citations ?? []}
              otherLinks={(answer[2] as ConversationEntryData | undefined)?.otherLinks ?? []}
              followupQuestions={(answer[2] as ConversationEntryData | undefined)?.followupQuestions ?? []}
              onInputQuestion={onInputQuestion}
            />
          )}
        </div>
        <div ref={messagesEndRef} />
      </div>
    );
  });

  const renderedAnswersWithStreaming = (
    <>
      {renderedAnswers}
      {generating && (
        <div className={styles.chatMessageWrapper}>
          <div className={styles.chatMessageGpt}>
            <StreamingAnswer
              state={{
                citations: streamState?.citations ?? [],
                otherLinks: streamState?.otherLinks ?? [],
                content: streamState?.content ?? "",
                followupQuestions: streamState?.followupQuestions ?? [],
                error: streamState?.error ? { error: streamState.error.message, recoverable: streamState.error.recoverable } : null,
                isStreaming: !(streamState?.isComplete ?? false)
              }}
              onCitationClicked={onCitationClicked}
              onFollowupClick={q => onInputQuestion(q.question)}
            />
          </div>
        </div>
      )}
    </>
  );

  const maybeGenerating = errorMsg && !isChatHome && !hasAnswer && !streamState && (
    <div className={styles.chatMessageGptMinWidth}>
      <AnswerStatus variant="error" message={errorMsg} />
    </div>
  );

  const headerButtons = (
    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
      <AthenaSearchButton disabled={generating} />
      <AthenaChatButton disabled={generating} />
      <SystemPrompt disabled={generating} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} isInside={inside} />
      <ConversationExporter disabled={generating} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} isInside={inside} />
      {!isChatHome && (
        <ClearChatButton onClick={clearChat} disabled={generating} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} isInside={inside} />
      )}
    </div>
  );

  const showChatHeader = !isChatHome && (
    <div className={styles.chatHeader}>
      <div className={styles.chatHeaderMenu}>
        <div className={styles.chatRefresh} onClick={clearChat}>
          <ServiceNSWLogo w={"123"} h={"63"} />
        </div>
        {headerButtons}
      </div>
    </div>
  );

  const showHomepageHeader = isChatHome && (
    <div className={styles.chatHeader}>
      <div className={styles.chatHeaderMenu}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <ServiceNSWLogo w={"123"} h={"63"} />
        </div>
        {headerButtons}
      </div>
    </div>
  );

  const content = isChatHome ? <ChatHome onQuestion={onInputQuestion} /> : renderedAnswersWithStreaming;

  const isVisible = usePageVisibility();

  useEffect(() => {
    if (isVisible && generating) {
      setInputFocusToggle(prev => !prev);
    }
  }, [isVisible, generating]);

  return (
    <>
      <div className={styles.container}>
        {showChatHeader}
        {showHomepageHeader}
        <div className={styles.chat}>
          <div className={styles.chatMessageBody}>
            {content}
            {maybeGenerating}
            <div ref={messagesEndRef} />
          </div>
        </div>
        <div className={styles.chatInput}>
          <QuestionInput
            clearOnSend
            placeholder="Message Sage..."
            disabled={generating}
            onSend={onInputQuestion}
            onFocus={isChatHome || hasAnswer}
            triggerFocus={inputFocusToggle}
          />
        </div>
        <p className={styles.footNote}>Sage can make mistakes. Always check the source links before advising customers.</p>
      </div>
    </>
  );
};

export default Chat;
